// src/components/admin/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { Layout, Row, Col, Card, Typography, Button, message, Spin } from "antd";
import AdminHeader from "./AdminHeader";

// local component imports (same folder)
import StatCard from "./StatCard";
import LineChart from "./LineChart";
import PieChart from "./PieChart";
import MapChart from "./MapChart";
import PendingTable from "./PendingTable";

// config (project root: src/config.js)
import { config } from "../../config";

const { Content } = Layout;
const { Title } = Typography;

// read defaults from config (with safe fallbacks)
const DASH = config?.dashboard || {};
const COUNTRY_CENTROIDS = DASH.countryCentroids || {
  India: [78.9629, 20.5937],
  USA: [-98.5795, 39.8283],
};

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    today_signup: 0,
    total_users: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
  });
  const [trend, setTrend] = useState({ dates: [], counts: [] });
  const [byCountry, setByCountry] = useState([]);
  const [byWorkplace, setByWorkplace] = useState([]);
  const [approvedCountries, setApprovedCountries] = useState([]);
  const [recentPending, setRecentPending] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const apiBase = (
  window.__APP_CONFIG__?.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8000"
).replace(/\/$/, ""); // remove trailing slash if any
  const endpointsCfg = DASH.endpoints || {};
  const endpoints = {
    stats: apiBase + (endpointsCfg.stats || "/admin_stats.php"),
    last7: apiBase + (endpointsCfg.last7 || "/signups_last7.php"),
    country: apiBase + (endpointsCfg.country || "/country_signups.php"),
    approvedCountries: apiBase + (endpointsCfg.approvedCountries || "/approved_countries.php"),
    pending: apiBase + (endpointsCfg.pending || "/list_pending.php"),
  };

  const token = localStorage.getItem("admin_token") || null;

  // Safe fetch that returns parsed JSON or throws with raw text
  const fetchJsonSafe = async (url, opts = {}) => {
    const res = await fetch(url, opts);
    const raw = await res.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch (e) {
      throw new Error(`Invalid JSON from ${url}: ${raw || "<empty>"}`);
    }
    if (!res.ok) {
      throw new Error(data?.error || `HTTP ${res.status} from ${url}`);
    }
    return data;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };

      const [
        statsRespResult,
        last7RespResult,
        countryRespResult,
        approvedRespResult,
        pendingRespResult,
      ] = await Promise.allSettled([
        fetchJsonSafe(endpoints.stats, { method: "GET", headers }),
        fetchJsonSafe(endpoints.last7, { method: "GET", headers }),
        fetchJsonSafe(endpoints.country, { method: "GET", headers }),
        fetchJsonSafe(endpoints.approvedCountries, { method: "GET", headers }),
        fetchJsonSafe(endpoints.pending, { method: "GET", headers }),
      ]);

      // stats
      if (statsRespResult.status === "fulfilled") {
        const d = statsRespResult.value;
        if (d?.ok && d.stats) {
          setStats({
            today_signup: d.stats.today_signup ?? d.stats.todaySignups ?? 0,
            total_users: d.stats.total_users ?? d.stats.totalUsers ?? 0,
            approved: d.stats.approved ?? 0,
            pending: d.stats.pending ?? 0,
            rejected: d.stats.rejected ?? 0,
          });
        } else {
          console.warn("admin stats endpoint returned unexpected shape", d);
        }
      } else {
        console.error("stats error", statsRespResult.reason);
        messageApi.error(`Stats: ${statsRespResult.reason?.message || "failed"}`);
      }

      // last7
      if (last7RespResult.status === "fulfilled") {
        const d = last7RespResult.value;
        if (d?.ok && Array.isArray(d.dates) && Array.isArray(d.counts)) {
          setTrend({ dates: d.dates, counts: d.counts });
        } else {
          console.warn("last7 endpoint returned unexpected shape", d);
        }
      } else {
        console.error("last7 error", last7RespResult.reason);
      }

      // country
      if (countryRespResult.status === "fulfilled") {
        const d = countryRespResult.value;
        if (d?.ok) {
          setByCountry(Array.isArray(d.by_country) ? d.by_country : (d.byCountry || []));
          setByWorkplace(Array.isArray(d.by_workplace_country) ? d.by_workplace_country : (d.byWorkplaceCountry || []));
        } else {
          console.warn("country endpoint unexpected", d);
        }
      } else {
        console.error("country error", countryRespResult.reason);
      }

      // approved countries
      if (approvedRespResult.status === "fulfilled") {
        const d = approvedRespResult.value;
        if (d?.ok) {
          setApprovedCountries(Array.isArray(d.approved_by_country) ? d.approved_by_country : (d.approvedByCountry || []));
        } else {
          console.warn("approved endpoint unexpected", d);
        }
      } else {
        console.error("approvedCountries error", approvedRespResult.reason);
      }

      // pending list
      if (pendingRespResult.status === "fulfilled") {
        const d = pendingRespResult.value;
        if (d?.ok && Array.isArray(d.pending)) {
          const limit = Number(DASH.pendingLimit ?? 8);
          setRecentPending(d.pending.slice(0, limit));
        } else {
          console.warn("pending endpoint unexpected", d);
        }
      } else {
        console.error("pending error", pendingRespResult.reason);
      }
    } catch (err) {
      console.error("loadAll error", err);
      messageApi.error(err?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // map markers from approvedCountries
  const markers = (approvedCountries || []).map((r) => {
    const country = r.country;
    const count = r.count || 0;
    const coords = (DASH.countryCentroids && DASH.countryCentroids[country]) || COUNTRY_CENTROIDS[country] || [0, 0];
    return { name: country, coordinates: coords, count };
  });

  // stat cards config array
  const statCardsCfg = DASH.statCards || [
    { key: "today_signup", title: "Today Signup", fallback: 0 },
    { key: "total_users", title: "Total Signup", fallback: 0 },
    { key: "approved", title: "Approved Users", fallback: 0 },
    { key: "pending", title: "Pending Users", fallback: 0 },
    { key: "rejected", title: "Rejected Users", fallback: 0 },
  ];

  const titles = DASH.chartTitles || {
    trend: "Signups - Last 7 Days",
    byCountry: "Signups by Country",
    byWorkplace: "Signups by Workplace Country",
    worldDistribution: "IAS Alumni Worldwide Distribution",
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f6fa" }}>
      <AdminHeader />
      <Content style={{ padding: "24px 40px" }}>
        {contextHolder}
        <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Title level={4} style={{ margin: 0 }}>Overview</Title>
          <div>
            <Button onClick={() => loadAll()} loading={loading}>
              Refresh
            </Button>
          </div>
        </div>

        <Spin spinning={loading && !trend.dates.length}>
          {/* Stat cards */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between", marginBottom: 20 }}>
            {statCardsCfg.map((c, idx) => (
              <StatCard key={c.key || idx} title={c.title} value={stats[c.key] ?? c.fallback} />
            ))}
          </div>

          {/* Trend line */}
          <Card bordered style={{ marginBottom: 20 }}>
            <LineChart title={titles.trend} categories={trend.dates} data={trend.counts} />
          </Card>

          {/* Donut + pie */}
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col xs={24} lg={12}>
              <Card bordered>
                <PieChart title={titles.byCountry} data={(byCountry || []).map((r) => ({ name: r.country, y: r.count }))} innerSize={50} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card bordered>
                <PieChart title={titles.byWorkplace} data={(byWorkplace || []).map((r) => ({ name: r.country, y: r.count }))} />
              </Card>
            </Col>
          </Row>

          {/* World map */}
          <Card bordered style={{ marginBottom: 20 }}>
            <Title level={5}>{titles.worldDistribution}</Title>
            <MapChart markers={markers} />
          </Card>

        
        </Spin>
      </Content>
    </Layout>
  );
}
